import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { clearStaleIdentity, trackPageView } from '@/lib/analytics';
import { routeName } from '@/lib/routeName';

export const PAGE_VIEW_SETTLE_MS = 100;

/**
 * One `page_viewed` per page the user lands on, sent only once the session is
 * known. Public routes never mount ProtectedRoute, so a lapsed session's
 * identity is dropped here, before the first view.
 */
export function usePageViews(session: { isPending: boolean; signedIn: boolean }) {
  const { isPending, signedIn } = session;
  const signedOut = !isPending && !signedIn;
  useEffect(() => {
    if (signedOut) clearStaleIdentity();
  }, [signedOut]);

  const { pathname } = useLocation();
  // A signed-out refetch flips isPending again; the path guard keeps one view per page.
  const trackedPath = useRef<string | null>(null);
  useEffect(() => {
    if (isPending || trackedPath.current === pathname) return;
    // Wait out a <Navigate>: a redirect-only route changes the path first and
    // cancels this, so only the page the user lands on is counted.
    const timer = setTimeout(() => {
      trackedPath.current = pathname;
      trackPageView(routeName(pathname));
    }, PAGE_VIEW_SETTLE_MS);
    return () => clearTimeout(timer);
  }, [pathname, isPending]);
}
